import React, { useState, useEffect } from 'react'
import toast from 'react-hot-toast'
import {
  CurrencyDollarIcon,
  DocumentTextIcon,
  CheckCircleIcon,
  XCircleIcon,
  EyeIcon,
  ArrowPathIcon,
  MagnifyingGlassIcon,
  AdjustmentsHorizontalIcon,
  CalendarIcon,
  BanknotesIcon,
  ExclamationTriangleIcon,
  CheckBadgeIcon
} from '@heroicons/react/24/outline'
import { Payment, PaymentStatus } from '../../types'
import { paymentService, PaymentVerificationData } from '../../services/payment.service'

export default function PaymentVerificationPage() {
  const [payments, setPayments] = useState<Payment[]>([])
  const [allPayments, setAllPayments] = useState<Payment[]>([])
  const [filteredPayments, setFilteredPayments] = useState<Payment[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)

  // Filter states
  const [statusFilter, setStatusFilter] = useState<string>('ALL')
  const [searchTerm, setSearchTerm] = useState('')

  // Modal states
  const [selectedPayment, setSelectedPayment] = useState<Payment | null>(null)
  const [showVerificationModal, setShowVerificationModal] = useState(false)
  const [showReceiptModal, setShowReceiptModal] = useState(false)
  const [verificationNotes, setVerificationNotes] = useState('')
  const [rejectionReason, setRejectionReason] = useState('')
  const [verificationAction, setVerificationAction] = useState<'VERIFIED' | 'REJECTED' | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)

  useEffect(() => {
    loadAllPaymentsData()
  }, [])

  useEffect(() => {
    filterPayments()
  }, [payments, statusFilter, searchTerm])

  // Auto-refresh when window regains focus (when user switches back from another tab)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden && !isLoading) {
        loadAllPaymentsData()
      }
    }

    document.addEventListener('visibilitychange', handleVisibilityChange)
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange)
  }, [isLoading])

  const loadAllPaymentsData = async () => {
    try {
      setIsLoading(true)
      setError(null)

      // Load all payments for statistics
      const allPaymentsData = await paymentService.getAllBankTransferPayments()
      setAllPayments(allPaymentsData)

      // Load only pending payments for verification
      const pendingPayments = await paymentService.getPendingPayments()
      setPayments(pendingPayments)
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load payments')
    } finally {
      setIsLoading(false)
    }
  }

  const filterPayments = () => {
    let filtered = payments && Array.isArray(payments) ? [...payments] : []

    // Filter by status
    if (statusFilter !== 'ALL') {
      filtered = filtered.filter(p => p.status === statusFilter)
    }

    // Filter by search term
    if (searchTerm) {
      filtered = filtered.filter(p =>
        p.caseNumber?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        p.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        p.user?.firstName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        p.user?.lastName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        p.transactionReference?.toLowerCase().includes(searchTerm.toLowerCase())
      )
    }

    setFilteredPayments(filtered)
  }

  const handleVerifyPayment = (payment: Payment) => {
    setSelectedPayment(payment)
    setVerificationAction('VERIFIED')
    setVerificationNotes('')
    setRejectionReason('')
    setShowVerificationModal(true)
  }

  const handleRejectPayment = (payment: Payment) => {
    setSelectedPayment(payment)
    setVerificationAction('REJECTED')
    setVerificationNotes('')
    setRejectionReason('')
    setShowVerificationModal(true)
  }

  const handleViewReceipt = (payment: Payment) => {
    if (payment.transferProofUrl || payment.receiptUrl) {
      setSelectedPayment(payment)
      setShowReceiptModal(true)
    } else {
      toast.error('No receipt uploaded for this payment')
    }
  }

  const submitVerification = async () => {
    if (!selectedPayment || !verificationAction) return

    try {
      setIsSubmitting(true)
      setError(null)

      const verificationData: PaymentVerificationData = {
        paymentId: selectedPayment.id,
        verificationStatus: verificationAction
      }

      // Only add optional fields if they have content
      if (verificationNotes && verificationNotes.trim()) {
        verificationData.verificationNotes = verificationNotes.trim()
      }

      if (verificationAction === 'REJECTED' && rejectionReason) {
        verificationData.rejectionReason = rejectionReason
      }

      const updatedPayment = await paymentService.verifyPayment(verificationData)

      // Update local state - handle case where API might not return updated payment
      if (updatedPayment && updatedPayment.id) {
        setPayments(prev => prev.map(p =>
          p.id === updatedPayment.id ? updatedPayment : p
        ))
      } else {
        // If no updated payment returned, remove the payment from pending list and refresh data
        setPayments(prev => prev.filter(p => p.id !== selectedPayment.id))
        // Refresh the data after a short delay
        setTimeout(() => {
          loadAllPaymentsData()
        }, 1000)
      }

      setSuccess(`Payment ${verificationAction === 'VERIFIED' ? 'verified' : 'rejected'} successfully!`)
      setShowVerificationModal(false)
      setSelectedPayment(null)
      setVerificationNotes('')
      setRejectionReason('')

      setTimeout(() => setSuccess(null), 3000)

    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to process verification')
    } finally {
      setIsSubmitting(false)
    }
  }

  const getStatusColor = (status: PaymentStatus): string => {
    return paymentService.getPaymentStatusColor(status)
  }

  const getStatusIcon = (status: PaymentStatus): string => {
    return paymentService.getPaymentStatusIcon(status)
  }

  const getVerificationStats = () => {
    if (!allPayments || !Array.isArray(allPayments)) {
      return {
        pending: 0,
        verified: 0,
        rejected: 0,
        total: 0
      }
    }
    const stats = {
      pending: allPayments.filter(p => p.status === 'PENDING').length,
      verified: allPayments.filter(p => p.status === 'PAID').length,
      rejected: allPayments.filter(p => p.status === 'REFUNDED').length,
      total: (allPayments && allPayments.length) || 0
    }
    return stats
  }

  const stats = getVerificationStats()

  if (isLoading && (!payments || payments.length === 0)) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-neutral-900 flex items-center">
              <CurrencyDollarIcon className="h-8 w-8 mr-3 text-primary-600" />
              Payment Verification
            </h1>
            <p className="mt-2 text-neutral-600">
              Review and verify user payment submissions
            </p>
          </div>
          <button
            onClick={loadAllPaymentsData}
            className="btn-secondary flex items-center"
            disabled={isLoading}
          >
            <ArrowPathIcon className="h-4 w-4 mr-2" />
            Refresh
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="card">
          <div className="card-body">
            <div className="flex items-center">
              <div className="flex-1">
                <p className="text-sm font-medium text-neutral-600">Total Pending</p>
                <p className="text-2xl font-bold text-yellow-600">{stats.pending}</p>
              </div>
              <div className="p-3 bg-yellow-100 rounded-lg">
                <ExclamationTriangleIcon className="h-6 w-6 text-yellow-600" />
              </div>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="card-body">
            <div className="flex items-center">
              <div className="flex-1">
                <p className="text-sm font-medium text-neutral-600">Verified</p>
                <p className="text-2xl font-bold text-green-600">{stats.verified}</p>
              </div>
              <div className="p-3 bg-green-100 rounded-lg">
                <CheckBadgeIcon className="h-6 w-6 text-green-600" />
              </div>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="card-body">
            <div className="flex items-center">
              <div className="flex-1">
                <p className="text-sm font-medium text-neutral-600">Rejected</p>
                <p className="text-2xl font-bold text-red-600">{stats.rejected}</p>
              </div>
              <div className="p-3 bg-red-100 rounded-lg">
                <XCircleIcon className="h-6 w-6 text-red-600" />
              </div>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="card-body">
            <div className="flex items-center">
              <div className="flex-1">
                <p className="text-sm font-medium text-neutral-600">Total</p>
                <p className="text-2xl font-bold text-neutral-600">{stats.total}</p>
              </div>
              <div className="p-3 bg-neutral-100 rounded-lg">
                <BanknotesIcon className="h-6 w-6 text-neutral-600" />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Success/Error Messages */}
      {success && (
        <div className="p-4 bg-green-50 border border-green-200 rounded-md">
          <div className="flex items-center text-green-800">
            <CheckCircleIcon className="h-5 w-5 mr-2" />
            <span className="text-sm font-medium">Success</span>
          </div>
          <p className="text-sm text-green-700 mt-1">{success}</p>
        </div>
      )}

      {error && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-md">
          <div className="flex items-center text-red-800">
            <XCircleIcon className="h-5 w-5 mr-2" />
            <span className="text-sm font-medium">Error</span>
          </div>
          <p className="text-sm text-red-700 mt-1">{error}</p>
        </div>
      )}

      {/* Filters */}
      <div className="card">
        <div className="card-body">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div className="flex items-center space-x-4">
              <div className="relative">
                <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-neutral-400" />
                <input
                  type="text"
                  placeholder="Search by case, name, or transfer reference..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="form-input pl-10"
                />
              </div>
              <button
                onClick={loadAllPaymentsData}
                disabled={isLoading}
                className="btn-secondary flex items-center"
                title="Refresh payments"
              >
                <ArrowPathIcon className={`h-4 w-4 mr-2 ${isLoading ? 'animate-spin' : ''}`} />
                Refresh
              </button>

              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="form-select"
              >
                <option value="ALL">All Status</option>
                <option value="PENDING">Pending</option>
                <option value="PAID">Verified</option>
                <option value="REFUNDED">Rejected</option>
              </select>
            </div>

            <div className="flex items-center text-sm text-neutral-500">
              <AdjustmentsHorizontalIcon className="h-4 w-4 mr-1" />
              {filteredPayments.length} of {payments && payments.length ? payments.length : 0} payments
            </div>
          </div>
        </div>
      </div>

      {/* Payments List */}
      <div className="card">
        <div className="card-header">
          <h3 className="text-lg font-medium text-neutral-900 flex items-center">
            <DocumentTextIcon className="h-5 w-5 mr-2 text-primary-600" />
            Payment Submissions
          </h3>
        </div>
        <div className="card-body">
          {filteredPayments.length === 0 ? (
            <div className="text-center py-8">
              <BanknotesIcon className="h-12 w-12 text-neutral-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-neutral-900 mb-2">No payments found</h3>
              <p className="text-neutral-600">
                {(!payments || payments.length === 0)
                  ? "There are no payment submissions to review."
                  : "No payments match your current filters."
                }
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {filteredPayments.map((payment) => (
                <div key={payment.id} className="border border-neutral-200 rounded-lg p-6">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center space-x-3 mb-4">
                        <span className="text-2xl">{getStatusIcon(payment.status)}</span>
                        <div>
                          <p className="font-semibold text-neutral-900">{payment.description}</p>
                          <p className="text-sm text-neutral-600">Case {payment.caseNumber}</p>
                        </div>
                        <div className="flex items-center space-x-2">
                          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(payment.status)}`}>
                            {paymentService.formatPaymentStatus(payment.status)}
                          </span>
                          {payment.status === 'PENDING' && (
                            <span className="inline-flex items-center px-2 py-1 text-xs font-medium rounded-full bg-blue-100 text-blue-800">
                              <BanknotesIcon className="h-3 w-3 mr-1" />
                              Awaiting Verification
                            </span>
                          )}
                        </div>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
                        <div>
                          <p className="text-sm font-medium text-neutral-700">Amount</p>
                          <p className="font-semibold text-lg text-primary-600">RM {payment.amount.toFixed(2)}</p>
                        </div>
                        <div>
                          <p className="text-sm font-medium text-neutral-700">Transfer Reference</p>
                          <p className="font-medium text-blue-600">
                            {payment.transactionReference || 'N/A'}
                          </p>
                        </div>
                        <div>
                          <p className="text-sm font-medium text-neutral-700">Submitted by</p>
                          <p className="font-medium">
                            {payment.user?.firstName} {payment.user?.lastName}
                          </p>
                          <p className="text-sm text-neutral-600">{payment.user?.email}</p>
                        </div>
                        <div>
                          <p className="text-sm font-medium text-neutral-700">Submitted</p>
                          <p className="font-medium">
                            {new Date(payment.createdAt).toLocaleDateString()}
                          </p>
                          <p className="text-sm text-neutral-600">
                            {new Date(payment.createdAt).toLocaleTimeString()}
                          </p>
                        </div>
                      </div>

                      {(payment.receiptUrl || payment.transferProofUrl) && (
                        <div className="mb-4">
                          <p className="text-sm font-medium text-neutral-700 mb-2">Receipt</p>
                          <button
                            onClick={() => handleViewReceipt(payment)}
                            className="btn-secondary text-sm flex items-center"
                          >
                            <EyeIcon className="h-4 w-4 mr-1" />
                            View Receipt
                          </button>
                        </div>
                      )}

                      {payment.verificationNotes && (
                        <div className="mb-4 p-3 bg-blue-50 rounded-md">
                          <p className="text-sm font-medium text-blue-900">Verification Notes:</p>
                          <p className="text-sm text-blue-700">{payment.verificationNotes}</p>
                        </div>
                      )}

                      {payment.rejectionReason && (
                        <div className="mb-4 p-3 bg-red-50 rounded-md">
                          <p className="text-sm font-medium text-red-900">Rejection Reason:</p>
                          <p className="text-sm text-red-700">{payment.rejectionReason}</p>
                        </div>
                      )}
                    </div>

                    <div className="flex space-x-2 ml-4">
                      {payment.status === 'PENDING' && (
                        <>
                          <button
                            onClick={() => handleVerifyPayment(payment)}
                            className="btn-primary text-sm flex items-center"
                          >
                            <CheckCircleIcon className="h-4 w-4 mr-1" />
                            Verify
                          </button>
                          <button
                            onClick={() => handleRejectPayment(payment)}
                            className="bg-red-600 hover:bg-red-700 text-white text-sm px-3 py-2 rounded-md flex items-center"
                          >
                            <XCircleIcon className="h-4 w-4 mr-1" />
                            Reject
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Verification Modal */}
      {showVerificationModal && selectedPayment && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-md w-full">
            <div className="p-6">
              <h3 className="text-lg font-semibold text-neutral-900 mb-4">
                {verificationAction === 'VERIFIED' ? 'Verify Payment' : 'Reject Payment'}
              </h3>

              <div className="mb-6">
                <div className="bg-neutral-50 p-4 rounded-lg">
                  <p className="font-medium">{selectedPayment.description}</p>
                  <p className="text-sm text-neutral-600">Case {selectedPayment.caseNumber}</p>
                  <p className="font-semibold text-primary-600 mt-2">
                    RM {selectedPayment.amount.toFixed(2)}
                  </p>
                </div>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-1">
                    Verification Notes
                  </label>
                  <textarea
                    value={verificationNotes}
                    onChange={(e) => setVerificationNotes(e.target.value)}
                    rows={3}
                    className="form-textarea"
                    placeholder="Add any notes about this verification..."
                  />
                </div>

                {verificationAction === 'REJECTED' && (
                  <div>
                    <label className="block text-sm font-medium text-neutral-700 mb-1">
                      Rejection Reason *
                    </label>
                    <textarea
                      value={rejectionReason}
                      onChange={(e) => setRejectionReason(e.target.value)}
                      rows={3}
                      className="form-textarea"
                      placeholder="Please specify the reason for rejection..."
                      required
                    />
                  </div>
                )}
              </div>

              <div className="flex justify-end space-x-3 mt-6">
                <button
                  onClick={() => setShowVerificationModal(false)}
                  className="btn-secondary"
                  disabled={isSubmitting}
                >
                  Cancel
                </button>
                <button
                  onClick={submitVerification}
                  className={`btn-primary ${verificationAction === 'REJECTED' ? 'bg-red-600 hover:bg-red-700' : ''}`}
                  disabled={isSubmitting || (verificationAction === 'REJECTED' && !rejectionReason.trim())}
                >
                  {isSubmitting ? 'Processing...' : verificationAction === 'VERIFIED' ? 'Verify Payment' : 'Reject Payment'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Receipt Modal */}
      {showReceiptModal && (selectedPayment?.transferProofUrl || selectedPayment?.receiptUrl) && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-4xl w-full max-h-screen overflow-hidden">
            <div className="p-6 border-b border-neutral-200 flex justify-between items-center">
              <h3 className="text-lg font-semibold text-neutral-900">Payment Receipt</h3>
              <button
                onClick={() => setShowReceiptModal(false)}
                className="btn-secondary"
              >
                Close
              </button>
            </div>
            <div className="p-6">
              {(() => {
                const receiptUrl = selectedPayment.transferProofUrl || selectedPayment.receiptUrl;
                return receiptUrl?.endsWith('.pdf') ? (
                  <iframe
                    src={receiptUrl}
                    className="w-full h-96 border border-neutral-200 rounded"
                    title="Payment Receipt PDF"
                  />
                ) : (
                  <img
                    src={receiptUrl}
                    alt="Payment Receipt"
                    className="max-w-full h-auto rounded"
                  />
                );
              })()}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}